import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PROJECTS as ALL_PROJECTS } from "@/data/portfolio";
import { PROJECTS } from "@/constants/testIds";
import SectionHeading from "@/components/molecules/SectionHeading";
import { FiX, FiExternalLink } from "react-icons/fi";

export default function Projects() {
  const categories = useMemo(
    () => ["All", ...Array.from(new Set(ALL_PROJECTS.map((p) => p.category)))],
    []
  );
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  // Lock body scroll + hide navbar interactions while modal open
  useEffect(() => {
    if (active) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [active]);

  const filtered = filter === "All" ? ALL_PROJECTS : ALL_PROJECTS.filter((p) => p.category === filter);

  return (
    <section id="projects" data-testid={PROJECTS.section} className="section relative">
      <div className="container-x">
        <SectionHeading
          overline="04 — Selected Work"
          title="Shipped products. Real users. Multiple industries."
          description="A glimpse into enterprise products I've architected and delivered — click a card for the case study."
        />

        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={PROJECTS.filter(c.toLowerCase().replace(/\s+/g, "-"))}
              onClick={() => setFilter(c)}
              className={`label-mono px-4 py-2 rounded-full border transition ${
                filter === c
                  ? "border-[color:var(--accent)] text-[color:var(--accent)] bg-[color:var(--accent)]/5"
                  : "border-[color:var(--border)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.button
                layout
                key={p.id}
                data-testid={PROJECTS.card(p.id)}
                onClick={() => setActive(p)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                whileHover={{ y: -6 }}
                className="group text-left rounded-3xl overflow-hidden border border-[color:var(--border)] bg-[color:var(--surface)] hover:border-[color:var(--accent)]/40 transition-colors"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bg)] via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 label-mono px-2 py-1 rounded-full bg-[color:var(--bg)]/70 backdrop-blur border border-[color:var(--border)]">
                    {p.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl mb-2">{p.name}</h3>
                  <p className="text-sm text-[color:var(--text-muted)] font-body line-clamp-2">
                    {p.short}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {p.stack.slice(0, 4).map((t) => (
                      <span
                        key={t}
                        className="px-2 py-0.5 rounded-full bg-[color:var(--bg)] border border-[color:var(--border)] text-[10px] font-mono text-[color:var(--text-muted)]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="label-mono text-[color:var(--text-muted)] group-hover:text-[color:var(--accent)] transition">
                      View case study →
                    </span>
                  </div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Modal — portal to escape <main z-10> stacking context */}
      {createPortal(
        <AnimatePresence>
          {active && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActive(null)}
              className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-xl flex items-center justify-center p-4 md:p-8 overflow-y-auto"
            >
              <motion.div
                onClick={(e) => e.stopPropagation()}
                initial={{ opacity: 0, scale: 0.95, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: "spring", damping: 26, stiffness: 220 }}
                className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl border border-[color:var(--border-strong)] bg-[color:var(--surface)]"
              >
                <button
                  data-testid={PROJECTS.modalClose}
                  onClick={() => setActive(null)}
                  aria-label="Close"
                  className="absolute top-4 right-4 z-[130] w-11 h-11 rounded-full bg-[color:var(--bg)]/90 backdrop-blur border border-[color:var(--border-strong)] flex items-center justify-center hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition"
                >
                  <FiX />
                </button>
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img src={active.image} alt={active.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--surface)] via-transparent" />
                </div>
                <div className="p-6 md:p-10">
                  <span className="label-mono text-[color:var(--accent)]">{active.category}</span>
                  <h3 className="font-display text-3xl md:text-5xl mt-3 mb-4">{active.name}</h3>
                  <p className="text-base md:text-lg text-[color:var(--text-muted)] font-body leading-relaxed">
                    {active.long}
                  </p>
                  <div className="mt-6 grid grid-cols-2 gap-4 max-w-md">
                    <div>
                      <div className="label-mono mb-1">Role</div>
                      <div className="font-mono text-sm">{active.role}</div>
                    </div>
                    <div>
                      <div className="label-mono mb-1">Domain</div>
                      <div className="font-mono text-sm">{active.category}</div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <div className="label-mono mb-3">Stack</div>
                    <div className="flex flex-wrap gap-2">
                      {active.stack.map((t) => (
                        <span
                          key={t}
                          className="px-3 py-1 rounded-full bg-[color:var(--bg)] border border-[color:var(--border)] text-xs font-mono"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </section>
  );
}
